import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { contractConfigApi } from '../services/api';
import type { ProjectFundingContract, ProjectFundingStatus } from '../types';
import { useRefreshOnReturn } from './useRefreshOnReturn';

export type ProjectFundingAction = ProjectFundingContract & {
  projectId: string;
  projectDescription: string;
};

/** A contract the person can pay right now and that still needs funding. */
export const isPayableFundingAction = (action: ProjectFundingContract) =>
  action.canPay && action.remainingFundingNeedOre > 0;

/**
 * Per-contract funding state for the homeowner's projects, kept fresh while the
 * page is open: reloads when the user returns to the tab (e.g. after an
 * external payment flow) and on payment events for projects that currently
 * have something to pay. Consumers decide which contracts to act on (typically
 * `isPayableFundingAction`).
 */
export function useProjectFundingActions(
  projects: Array<{ id: string; description: string }>,
  refreshToken?: unknown
): { actions: ProjectFundingAction[]; fundingByProject: Record<string, ProjectFundingStatus> } {
  const [fundingByProject, setFundingByProject] = useState<Record<string, ProjectFundingStatus>>({});
  const actions = useMemo(() => projects.flatMap(project =>
    (fundingByProject[project.id]?.contracts ?? []).map(contract => ({
      ...contract, projectId: project.id, projectDescription: project.description
    }))), [projects, fundingByProject]);
  const requestVersion = useRef(0);
  const load = useCallback(async () => {
    const version = ++requestVersion.current;
    const results = await Promise.all(projects.map(async project => {
      return [project.id, await contractConfigApi.getProjectFunding(project.id)] as const;
    }));
    if (requestVersion.current === version) setFundingByProject(Object.fromEntries(results));
  }, [projects]);
  useEffect(() => {
    const version = requestVersion;
    void load().catch(() => {});
    return () => { version.current++; };
  }, [load, projects, refreshToken]);
  useRefreshOnReturn(load);

  // Live payment events only for projects with something to pay. Browsers cap
  // concurrent connections per host, so one open event stream per project
  // would starve the funding requests themselves for a person with many
  // projects — and only payable projects can change state through a payment.
  const payableProjectIds = useMemo(
    () => Array.from(new Set(actions.filter(isPayableFundingAction).map(action => action.projectId))).sort().join(','),
    [actions]
  );
  useEffect(() => {
    const ids = payableProjectIds ? payableProjectIds.split(',') : [];
    const unsubscribe = ids.map(projectId =>
      contractConfigApi.subscribeProjectPaymentEvents(projectId, () => { void load().catch(() => {}); })
    );
    return () => { unsubscribe.forEach(stop => stop()); };
  }, [load, payableProjectIds]);

  return { actions, fundingByProject };
}
